/**
 * Centralized data-fetching context for a single medical PA order.
 *
 * Aggregates seven Postgres-backed data domains (general order details,
 * auth-on-file, clinical data, comments, coverage, workflow config, and
 * reviews) into one provider. Each domain is backed by its own
 * {@link useAbortableFetch} instance so requests can be cancelled
 * independently, and a single `fetchAll` function triggers every domain
 * in parallel.
 *
 * The provider accepts only `children` — `orderId` and `orgId` are supplied
 * at call-time via the exposed `fetchAll(orderId, orgId)` function, giving
 * consumers full control over when data is fetched.
 *
 * @module dataAccumulatorForSingleOrder
 */
import React, { createContext, useCallback, useContext, useRef } from "react";
import { Authorization } from "../data-model/AuthorizationModel";
import { ClinicalEntry } from "../data-model/clinicalCondition";
import {
  AuthOnFile,
  ClinicalDataEntry,
  CoverageResult,
  GeneralOrderDetails,
  SingleCoverage,
} from "../data-model/medicalPaOrdersModel";
import { StatusLogDataWithId } from "../data-model/statusLogModel";
import { WorkflowConfig } from "../data-model/workflowConfig";
import { SubmissionDataResponse } from "../hooks/singleMedicalPaOrderDetails/useSubmissionInformation";
import { useAbortableFetch } from "../hooks/useAbortableFetch";
import { ReviewData } from "../../shared/api/postCall/saveReviewData";
import { singleOrderDetailsGenericCallForPostgres } from "../../shared/api/postgreSQL/postCalls/singleOrderDetailsGenericCallForPostgres";
import { SingleOrderDetailsTableName } from "../../shared/enums/singleOrderDetailsTableName";

// ---------------------------------------------------------------------------
// Response shapes (mirror the per-hook definitions)
// ---------------------------------------------------------------------------

/** Response from the general-order-details endpoint. */
interface GeneralOrderDetailsResponse {
  order: GeneralOrderDetails;
}

/** Response from the auth-on-file endpoint. */
interface AuthOnFileDetailsResponse {
  auth_on_file: AuthOnFile;
}

/** Response from the clinical-data endpoint. */
interface ClinicalDataResponse {
  entries: ClinicalDataEntry[];
}

/** Response from the order-comments endpoint. */
interface CommentsDataResponse {
  comments: StatusLogDataWithId[];
}

/** Response from the coverage endpoint. */
interface CoverageDetailsResponse {
  coverages: SingleCoverage[];
  coverage_results: CoverageResult[];
}

// /** Response from the workflow-config endpoint. */
// interface WorkflowConfigDetailsResponse {
//   workflow_config: WorkflowConfig;
// }

/** Response from the reviews endpoint. */
interface ReviewsDataResponse {
  reviews: ReviewData[];
}

/** Response from the clinical-conditions endpoint (ICD conditions list). */
interface ClinicalConditionsResponse {
  entries: ClinicalEntry[];
}

// ---------------------------------------------------------------------------
// Context value interface
// ---------------------------------------------------------------------------

/**
 * Shape of the value exposed by {@link DataAccumulatorContext}.
 *
 * Each domain exposes its data, loading flag, and error independently.
 * A combined `loading` flag and a `fetchAll` / `abortAll` function are
 * also provided. `fetchAll` accepts `orderId` and `orgId` at call-time.
 */
/** Signature for a single-domain fetch function exposed by the context. */
type DomainFetcher = (orderId: string, orgId: string) => Promise<unknown>;

interface DataAccumulatorContextValue {
  generalOrderDetails: GeneralOrderDetailsResponse | null;
  generalOrderDetailsLoading: boolean;
  generalOrderDetailsError: unknown;
  fetchGeneralOrderDetails: DomainFetcher;

  authOnFileDetails: AuthOnFileDetailsResponse | null;
  authOnFileDetailsLoading: boolean;
  authOnFileDetailsError: unknown;
  fetchAuthOnFileDetails: DomainFetcher;

  clinicalData: ClinicalDataResponse | null;
  clinicalDataLoading: boolean;
  clinicalDataError: unknown;
  fetchClinicalData: DomainFetcher;

  commentsData: CommentsDataResponse | null;
  commentsDataLoading: boolean;
  commentsDataError: unknown;
  fetchCommentsData: DomainFetcher;

  coverageDetails: CoverageDetailsResponse | null;
  coverageDetailsLoading: boolean;
  coverageDetailsError: unknown;
  fetchCoverageDetails: DomainFetcher;

  workflowConfig: WorkflowConfig | null;
  workflowConfigLoading: boolean;
  workflowConfigError: unknown;
  fetchWorkflowConfig: DomainFetcher;

  reviewsData: ReviewsDataResponse | null;
  reviewsDataLoading: boolean;
  reviewsDataError: unknown;
  fetchReviewsData: DomainFetcher;

  clinicalConditions: ClinicalConditionsResponse | null;
  clinicalConditionsLoading: boolean;
  clinicalConditionsError: unknown;
  fetchClinicalConditions: DomainFetcher;

  authorizationData: Authorization | null;
  authorizationDataLoading: boolean;
  authorizationDataError: unknown;
  fetchAuthorizationData: DomainFetcher;

  submissionData: SubmissionDataResponse | null;
  submissionDataLoading: boolean;
  submissionDataError: unknown;
  fetchSubmissionData: DomainFetcher;

  /** `true` while any domain fetch is in progress. */
  loading: boolean;

  /**
   * Triggers all domain fetches in parallel.
   *
   * @param orderId - PA order ID to fetch data for
   * @param orgId   - Organisation ID scoping the queries
   */
  fetchAll: (orderId: string, orgId: string) => Promise<void>;

  /** Aborts every in-flight domain request. */
  abortAll: () => void;
}

// ---------------------------------------------------------------------------
// Context + consumer hook
// ---------------------------------------------------------------------------

const noopFetcher: DomainFetcher = async () => {};

const DataAccumulatorContext = createContext<DataAccumulatorContextValue>({
  generalOrderDetails: null,
  generalOrderDetailsLoading: false,
  generalOrderDetailsError: null,
  fetchGeneralOrderDetails: noopFetcher,

  authOnFileDetails: null,
  authOnFileDetailsLoading: false,
  authOnFileDetailsError: null,
  fetchAuthOnFileDetails: noopFetcher,

  clinicalData: null,
  clinicalDataLoading: false,
  clinicalDataError: null,
  fetchClinicalData: noopFetcher,

  commentsData: null,
  commentsDataLoading: false,
  commentsDataError: null,
  fetchCommentsData: noopFetcher,

  coverageDetails: null,
  coverageDetailsLoading: false,
  coverageDetailsError: null,
  fetchCoverageDetails: noopFetcher,

  workflowConfig: null,
  workflowConfigLoading: false,
  workflowConfigError: null,
  fetchWorkflowConfig: noopFetcher,

  reviewsData: null,
  reviewsDataLoading: false,
  reviewsDataError: null,
  fetchReviewsData: noopFetcher,

  clinicalConditions: null,
  clinicalConditionsLoading: false,
  clinicalConditionsError: null,
  fetchClinicalConditions: noopFetcher,

  authorizationData: null,
  authorizationDataLoading: false,
  authorizationDataError: null,
  fetchAuthorizationData: noopFetcher,

  submissionData: null,
  submissionDataLoading: false,
  submissionDataError: null,
  fetchSubmissionData: noopFetcher,

  loading: false,
  fetchAll: async () => {},
  abortAll: () => {},
});

/**
 * Convenience hook to consume the data-accumulator context.
 *
 * @returns The current {@link DataAccumulatorContextValue}
 */
export const useDataAccumulatorForSingleOrder = () =>
  useContext(DataAccumulatorContext);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

/**
 * Provider that holds parallel Postgres data-fetch instances for a
 * single order.
 *
 * No data is fetched automatically — consumers call `fetchAll(orderId, orgId)`
 * to trigger all domains in parallel, and `abortAll()` to cancel every
 * in-flight request.
 *
 * @param props.children - React subtree to wrap
 */
export const DataAccumulatorForSingleOrderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // ---- fetch callbacks (orderId & orgId supplied at call-time) -------------

  const fetchGeneralOrderDetailsFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<GeneralOrderDetailsResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.MEDICAL_PA_ORDER_GENERAL_INFO,
        },
        signal,
      ),
    [],
  );

  const fetchAuthOnFileDetailsFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<AuthOnFileDetailsResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.AUTH_ON_FILE,
        },
        signal,
      ),
    [],
  );

  const fetchClinicalDataFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<ClinicalDataResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.CLINICAL_DATA,
        },
        signal,
      ),
    [],
  );

  const fetchCommentsDataFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<CommentsDataResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.ORDER_COMMENTS,
        },
        signal,
      ),
    [],
  );

  const fetchCoverageDetailsFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<CoverageDetailsResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.COVERAGE,
        },
        signal,
      ),
    [],
  );

  const fetchWorkflowConfigDetailsFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<WorkflowConfig>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.WORKFLOW_CONFIG,
        },
        signal,
      ),
    [],
  );

  const fetchReviewsDataFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<ReviewsDataResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.REVIEWS,
        },
        signal,
      ),
    [],
  );

  const fetchClinicalConditionsFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<ClinicalConditionsResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.CLINICAL_CONDITIONS,
        },
        signal,
      ),
    [],
  );

  const fetchAuthorizationDataFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<Authorization>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.AUTHORIZATION,
        },
        signal,
      ),
    [],
  );

  const fetchSubmissionDataFn = useCallback(
    (signal: AbortSignal, orderId: string, orgId: string) =>
      singleOrderDetailsGenericCallForPostgres<SubmissionDataResponse>(
        {
          order_id: orderId,
          org_id: orgId,
          table_name: SingleOrderDetailsTableName.SUBMISSION,
        },
        signal,
      ),
    [],
  );

  // ---- abortable-fetch instances ------------------------------------------

  const generalOrder = useAbortableFetch(fetchGeneralOrderDetailsFn);
  const authOnFile = useAbortableFetch(fetchAuthOnFileDetailsFn);
  const clinical = useAbortableFetch(fetchClinicalDataFn);
  const comments = useAbortableFetch(fetchCommentsDataFn);
  const coverage = useAbortableFetch(fetchCoverageDetailsFn);
  const workflowConfig = useAbortableFetch(fetchWorkflowConfigDetailsFn);
  const reviews = useAbortableFetch(fetchReviewsDataFn);
  const clinicalConditions = useAbortableFetch(fetchClinicalConditionsFn);
  const authorization = useAbortableFetch(fetchAuthorizationDataFn);
  const submission = useAbortableFetch(fetchSubmissionDataFn);

  // ---- dedup ref to prevent double-firing ----------------------------------

  /** Tracks the currently in-flight `fetchAll` key so duplicate calls are skipped. */
  const fetchInFlightRef = useRef<string | null>(null);

  // ---- orchestration helpers ----------------------------------------------

  const abortAll = useCallback(() => {
    fetchInFlightRef.current = null;
    generalOrder.abort();
    authOnFile.abort();
    clinical.abort();
    comments.abort();
    coverage.abort();
    workflowConfig.abort();
    reviews.abort();
    clinicalConditions.abort();
    authorization.abort();
    submission.abort();
  }, [
    generalOrder.abort,
    authOnFile.abort,
    clinical.abort,
    comments.abort,
    coverage.abort,
    workflowConfig.abort,
    reviews.abort,
    clinicalConditions.abort,
    authorization.abort,
    submission.abort,
  ]);

  const fetchAll = useCallback(
    async (orderId: string, orgId: string) => {
      const key = `${orderId}::${orgId}`;
      if (fetchInFlightRef.current === key) return;
      fetchInFlightRef.current = key;

      try {
        await Promise.all([
          generalOrder.runFetch(orderId, orgId),
          authOnFile.runFetch(orderId, orgId),
          clinical.runFetch(orderId, orgId),
          comments.runFetch(orderId, orgId),
          coverage.runFetch(orderId, orgId),
          workflowConfig.runFetch(orderId, orgId),
          reviews.runFetch(orderId, orgId),
          clinicalConditions.runFetch(orderId, orgId),
          authorization.runFetch(orderId, orgId),
          submission.runFetch(orderId, orgId),
        ]);
      } finally {
        if (fetchInFlightRef.current === key) {
          fetchInFlightRef.current = null;
        }
      }
    },
    [
      generalOrder.runFetch,
      authOnFile.runFetch,
      clinical.runFetch,
      comments.runFetch,
      coverage.runFetch,
      workflowConfig.runFetch,
      reviews.runFetch,
      clinicalConditions.runFetch,
      authorization.runFetch,
      submission.runFetch,
    ],
  );

  // ---- derived loading state ----------------------------------------------

  const loading =
    generalOrder.loading ||
    authOnFile.loading ||
    clinical.loading ||
    comments.loading ||
    coverage.loading ||
    workflowConfig.loading ||
    reviews.loading ||
    clinicalConditions.loading ||
    authorization.loading ||
    submission.loading;

  // ---- render -------------------------------------------------------------

  return (
    <DataAccumulatorContext.Provider
      value={{
        generalOrderDetails: generalOrder.data,
        generalOrderDetailsLoading: generalOrder.loading,
        generalOrderDetailsError: generalOrder.error,
        fetchGeneralOrderDetails: generalOrder.runFetch,

        authOnFileDetails: authOnFile.data,
        authOnFileDetailsLoading: authOnFile.loading,
        authOnFileDetailsError: authOnFile.error,
        fetchAuthOnFileDetails: authOnFile.runFetch,

        clinicalData: clinical.data,
        clinicalDataLoading: clinical.loading,
        clinicalDataError: clinical.error,
        fetchClinicalData: clinical.runFetch,

        commentsData: comments.data,
        commentsDataLoading: comments.loading,
        commentsDataError: comments.error,
        fetchCommentsData: comments.runFetch,

        coverageDetails: coverage.data,
        coverageDetailsLoading: coverage.loading,
        coverageDetailsError: coverage.error,
        fetchCoverageDetails: coverage.runFetch,

        workflowConfig: workflowConfig.data,
        workflowConfigLoading: workflowConfig.loading,
        workflowConfigError: workflowConfig.error,
        fetchWorkflowConfig: workflowConfig.runFetch,

        reviewsData: reviews.data,
        reviewsDataLoading: reviews.loading,
        reviewsDataError: reviews.error,
        fetchReviewsData: reviews.runFetch,

        clinicalConditions: clinicalConditions.data,
        clinicalConditionsLoading: clinicalConditions.loading,
        clinicalConditionsError: clinicalConditions.error,
        fetchClinicalConditions: clinicalConditions.runFetch,

        authorizationData: authorization.data,
        authorizationDataLoading: authorization.loading,
        authorizationDataError: authorization.error,
        fetchAuthorizationData: authorization.runFetch,

        submissionData: submission.data,
        submissionDataLoading: submission.loading,
        submissionDataError: submission.error,
        fetchSubmissionData: submission.runFetch,

        loading,
        fetchAll,
        abortAll,
      }}
    >
      {children}
    </DataAccumulatorContext.Provider>
  );
};
